import type { BreakTimeRule, BreakTimeSettings, Shift } from '../types';
import { extractTimezoneNaiveTime } from './datetime';

export function calculateShiftLengthHours(startTime: string, endTime: string): number | null {
  const start = extractTimezoneNaiveTime(startTime);
  const end = extractTimezoneNaiveTime(endTime);
  if (!start || !end) return null;

  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);
  if ([startHour, startMin, endHour, endMin].some((n) => Number.isNaN(n))) return null;

  const startTotal = startHour * 60 + startMin;
  let endTotal = endHour * 60 + endMin;
  if (endTotal < startTotal) endTotal += 24 * 60;
  return (endTotal - startTotal) / 60;
}

export function lookupRuleBreakMinutes(rules: BreakTimeRule[] | undefined, workedHours: number): number {
  if (!rules?.length) return 0;
  const matchingRule = rules.find(
    (rule) => workedHours >= rule.minHours && workedHours < rule.maxHours
  );
  return matchingRule?.breakMinutes ?? 0;
}

export function calculateDefaultBreakMinutes(
  startTime: string,
  endTime: string,
  rules: BreakTimeRule[] | undefined
): number | null {
  const totalHours = calculateShiftLengthHours(startTime, endTime);
  if (totalHours === null) return null;
  return lookupRuleBreakMinutes(rules, totalHours);
}

export function shiftUsesGlobalBreakSettings(shift: Shift | undefined): boolean {
  return shift?.breakUseGlobalSettings !== false;
}

export function resolveShiftBreakIsPaid(
  shift: Shift | undefined,
  breakTimeSettings: BreakTimeSettings | null
): boolean {
  if (!shift) return breakTimeSettings?.isPaid ?? false;
  if (shiftUsesGlobalBreakSettings(shift)) {
    return breakTimeSettings?.isPaid ?? false;
  }
  return shift.breakTimePaid ?? false;
}

export function resolveShiftBreakMinutes(
  shift: Shift | undefined,
  breakTimeSettings: BreakTimeSettings | null
): number | null {
  if (!shift) return null;
  if (shiftUsesGlobalBreakSettings(shift)) {
    return calculateDefaultBreakMinutes(shift.startTime, shift.endTime, breakTimeSettings?.rules);
  }
  return Math.round((shift.unpaidHours ?? 0) * 60);
}
